import { SVCheckbox, SVCurrencyInput } from '@/components/core';
import { CompanyFormFields } from '@/types/companies';
import { ProgramSettings } from '@/types/setting';
import { formatCurrency } from '@/utils/currency';
import { Card, Typography, Stack, Divider, Box } from '@mui/material';
import { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';

type FeeSectionProps = {
  programSettings?: ProgramSettings;
};

export default function FeeSection({ programSettings }: FeeSectionProps) {
  const { setValue, watch, getValues } = useFormContext<CompanyFormFields>();

  const useProgramIncomingWireFee = watch('useProgramIncomingWireFee');
  const useProgramOutgoingWireFee = watch('useProgramOutgoingWireFee');
  const useProgramMonthlySavingsFee = watch('useProgramMonthlySavingsFee');

  useEffect(() => {
    if (useProgramIncomingWireFee) {
      setValue('feeIncomingDomestic', '');
    } else {
      // Only set default value if there's no existing value
      const currentValue = getValues('feeIncomingDomestic');
      if (!currentValue) {
        setValue('feeIncomingDomestic', '0.00');
      }
    }
  }, [useProgramIncomingWireFee, setValue, getValues]);

  useEffect(() => {
    if (useProgramOutgoingWireFee) {
      setValue('feeOutcomeDomestic', '');
    } else {
      // Only set default value if there's no existing value
      const currentValue = getValues('feeOutcomeDomestic');
      if (!currentValue) {
        setValue('feeOutcomeDomestic', '0');
      }
    }
  }, [useProgramOutgoingWireFee, setValue, getValues]);

  useEffect(() => {
    if (useProgramMonthlySavingsFee) {
      setValue('feeMonthlySavingAccount', '');
    } else {
      // Only set default value if there's no existing value
      const currentValue = getValues('feeMonthlySavingAccount');
      if (!currentValue) {
        setValue('feeMonthlySavingAccount', '0');
      }
    }
  }, [useProgramMonthlySavingsFee, setValue, getValues]);

  return (
    <Card>
      <Typography variant="h5" mb={2}>
        Fees
      </Typography>
      <Stack divider={<Divider />} spacing={2}>
        <Box>
          <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
            Incoming Domestic Wire fee
          </Typography>
          <SVCheckbox
            name="useProgramIncomingWireFee"
            label={`Use Program Setting${
              programSettings?.feeIncomingDomestic?.value
                ? ` (Currently set as: ${formatCurrency({
                    amount: programSettings.feeIncomingDomestic.value,
                  })})`
                : ''
            }`}
          />
          {!useProgramIncomingWireFee && (
            <SVCurrencyInput
              name="feeIncomingDomestic"
              label={
                !useProgramIncomingWireFee ? 'Incoming Domestic Wire Fee' : ''
              }
              required={false}
              disabled={useProgramIncomingWireFee}
            />
          )}
          {!useProgramIncomingWireFee && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              This value will override the program settings fee.
            </Typography>
          )}
        </Box>
        <Box>
          <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
            Outgoing Domestic Wire fee
          </Typography>
          <SVCheckbox
            name="useProgramOutgoingWireFee"
            label={`Use Program Setting${
              programSettings?.feeOutcomeDomestic?.value
                ? ` (Currently set as: ${formatCurrency({
                    amount: programSettings.feeOutcomeDomestic.value,
                  })})`
                : ''
            }`}
          />
          {!useProgramOutgoingWireFee && (
            <SVCurrencyInput
              name="feeOutcomeDomestic"
              label={
                !useProgramOutgoingWireFee ? 'Outgoing Domestic Wire Fee' : ''
              }
              required={false}
              disabled={useProgramOutgoingWireFee}
            />
          )}
          {!useProgramOutgoingWireFee && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              This value will override the program settings fee.
            </Typography>
          )}
        </Box>
        <Box>
          <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
            Monthly Savings Account fee
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            The fee will be the same for an individual, joint, and business
            account.
          </Typography>
          <SVCheckbox
            name="useProgramMonthlySavingsFee"
            label={`Use Program Setting${
              programSettings?.feeMonthlySavingAccount?.value
                ? ` (Currently set as: ${formatCurrency({
                    amount: programSettings.feeMonthlySavingAccount.value,
                  })})`
                : ''
            }`}
          />
          {!useProgramMonthlySavingsFee && (
            <SVCurrencyInput
              name="feeMonthlySavingAccount"
              label={
                !useProgramMonthlySavingsFee
                  ? 'Monthly Savings Account Fee'
                  : ''
              }
              rules={{}}
              required={false}
              disabled={useProgramMonthlySavingsFee}
            />
          )}
          {!useProgramMonthlySavingsFee && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              This value will override the program settings fee.
            </Typography>
          )}
        </Box>
      </Stack>
    </Card>
  );
}
